import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { SubscriptionService, subscriptionError } from '../../core/services/subscription.service';
import { StudioService } from '../../core/services/studio.service';
import { StudioSelectionService } from '../../core/services/studio-selection.service';
import { PagerComponent } from '../../shared/components/pager.component';
import { deliveryDayLabel, subscriptionMonthLabel } from './subscription-date';
import { BusinessClock } from '../../core/time/business-clock.service';
import { JstDate } from '../../core/time/business-time';
import { PageTitleService } from '../../core/services/page-title.service';
import { matchesNestedKeyword } from '../../shared/utils/keyword-filter';
import { SubscriptionStateLegendComponent, subscriptionState } from './subscription-state-legend.component';

@Component({
    selector: 'app-subscription-list',
    imports: [CommonModule, FormsModule, RouterLink, PagerComponent, SubscriptionStateLegendComponent],
    templateUrl: './subscription-list.component.html'
})
export class SubscriptionListComponent implements OnInit {
  private service = inject(SubscriptionService);
  private studioService = inject(StudioService);
  private selection = inject(StudioSelectionService);
  private pageTitle = inject(PageTitleService);
  private readonly clock = inject(BusinessClock);
  private requestId = 0;
  studios: any[] = [];
  studio: any = null;
  items: any[] = [];
  includeEnded = false;
  query = '';
  searching = false;
  downloading = false;
  error = '';
  pageIndex = 0;
  readonly pageLimit = 50;
  readonly deliveryDayLabel = deliveryDayLabel;
  readonly state = subscriptionState;

  async ngOnInit(): Promise<void> {
    this.pageTitle.set('物販定期便', 'hand_package', true, 'material-symbols-outlined');
    try {
      this.studios = ((await this.studioService.list('all')) ?? []).filter((s: any) => String(s.id) !== 'all' && Number(s.status) !== 2);
      this.studio = this.selection.restore(this.studios);
      await this.load();
    } catch (error) { this.error = subscriptionError(error); }
  }

  async load(): Promise<void> {
    const requestId = ++this.requestId;
    this.items = [];
    this.pageIndex = 0;
    this.error = '';
    this.searching = !!this.studio?.id;
    if (!this.studio?.id) return;
    this.selection.save(this.studio);
    try {
      const items = await this.service.getByStudio(this.studio.id, this.includeEnded);
      if (requestId === this.requestId) this.items = Array.isArray(items) ? items : [];
    } catch (error) {
      if (requestId === this.requestId) this.error = subscriptionError(error);
    } finally { if (requestId === this.requestId) this.searching = false; }
  }

  get filteredItems(): any[] {
    return this.items.filter(item => matchesNestedKeyword(item, this.query));
  }
  get pagedItems(): any[] { return this.filteredItems.slice(this.pageIndex * this.pageLimit, (this.pageIndex + 1) * this.pageLimit); }
  get totalPages(): number { return Math.ceil(this.filteredItems.length / this.pageLimit); }

  async downloadCsv(): Promise<void> {
    if (this.downloading || this.searching || !this.studio || !this.filteredItems.length) return;
    this.downloading = true;
    this.error = '';
    try {
    if (!await this.clock.synchronize(true)) return;
    const now = new JstDate();
    const timestamp = [now.getFullYear(), now.getMonth() + 1, now.getDate(), now.getHours(), now.getMinutes(), now.getSeconds()]
      .map((value, index) => index ? String(value).padStart(2, '0') : String(value)).join('');
    const rows = [['会員ID', '氏名', '商品名', '金額（税込）', '引渡日', '開始月', '終了月'], ...this.filteredItems.map(item => [
      item.member?.memberCode ?? '', [item.member?.lastName, item.member?.firstName].filter(Boolean).join(' '),
      item.product?.name ?? '', item.taxIncAmount ?? '', deliveryDayLabel(item.deliveryDay, ''),
      subscriptionMonthLabel(item.startDate), subscriptionMonthLabel(item.endDate),
    ])];
    const csv = rows.map(row => row.map(value => `"${String(value).replace(/"/g, '""')}"`).join(',')).join('\r\n');
    const url = URL.createObjectURL(new Blob(['\uFEFF', csv], { type: 'text/csv;charset=utf-8' }));
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `${String(this.studio?.name ?? '').replace(/[\\/:*?"<>|]/g, '_')}_物販定期便_${timestamp}.csv`;
    try { anchor.click(); }
    finally { setTimeout(() => URL.revokeObjectURL(url), 0); }
    } catch (error) { this.error = subscriptionError(error); }
    finally { this.downloading = false; }
  }
}
